import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import PastPerformanceReportChart from 'components/Charts/Report/PastPerformanceReportChart';
import RiskScatterChart from 'components/Charts/Report/RiskScatterChart';
import React, { useState, useEffect } from 'react';
import TypicalAssetAllocationReportChart from 'components/Charts/Report/TypicalAssetAllocation';
import SectorBreakdownLegend from 'components/Breakdown/SectorBreakdownLegend';
import {
  mapSectorData,
  mapRegionalData
} from 'components/Charts/Utils/breakdowns';
import Store from 'store/store';
import CountryBreakdown from 'components/Charts/Report/CountryBreakdown';
import MorningstarComfortRangesReportChart from 'components/Charts/Report/MorningstarComfortRangesReportChart';

const { selectors } = Store;

const BackgroundCharts = ({
  investments,
  pastPerformance,
  cachedAssetAllocations,
  cachedCountryBreakdown,
  cachedSectorBreakdown,
  cachedRegionalBreakdown,
  setBackgroundChartsLoaded,
  imagesLoading,
  riskProfiler,
  riskRatings
}: any) => {
  const [isInitial, setIsInitial] = useState(true);

  useEffect(() => {
    if (imagesLoading.length === 0 && isInitial) {
      setIsInitial(false);
      return;
    }
    setBackgroundChartsLoaded(
      !imagesLoading.some((key: string) => key.startsWith('ia_'))
    );
  }, [imagesLoading.length]);

  return (
    <>
      {pastPerformance.tabularData &&
        Object.keys(pastPerformance.tabularData).length > 0 && (
          <RiskScatterChart
            data={investments}
            performanceData={pastPerformance.tabularData}
            name="ia_scatter"
          />
        )}
      {pastPerformance.data && pastPerformance.data.length > 0 && (
        <PastPerformanceReportChart
          name="ia_performance"
          data={pastPerformance.data}
          earliestDates={pastPerformance.earliestDates}
          footprints={pastPerformance.footprints}
          showOverview={false}
        />
      )}
      {riskProfiler === 'Morningstar' && (
        <MorningstarComfortRangesReportChart
          investments={investments}
          data={riskRatings.data}
        />
      )}
      {investments.map((fund: any) => {
        const prefix = fund.type === 'f' ? 'f' : 'mp';
        return (
          <>
            <TypicalAssetAllocationReportChart
              name={`ia_allocation_${fund.id}`}
              data={(
                cachedAssetAllocations.data[`${prefix}-${fund.id}`] || []
              ).map((allocation: any) => ({
                name: allocation.name,
                value: Number(allocation.value) * 100
              }))}
            />
            <TypicalAssetAllocationReportChart
              name={`ia_sector_breakdown_${fund.id}`}
              data={mapSectorData(
                cachedSectorBreakdown.data[`${prefix}-${fund.id}`] || []
              ).map((allocation: any) => ({
                name: allocation.name,
                value: Number(allocation.value) * 100
              }))}
              legend={SectorBreakdownLegend}
              isForReport={true}
            />
            <CountryBreakdown
              name={`ia_country_breakdown_${fund.id}`}
              data={(
                cachedCountryBreakdown.data[`${prefix}-${fund.id}`] || []
              ).map((allocation: any) => ({
                name: allocation.name,
                value: Number(allocation.value) * 100,
                equity: Number(allocation.equity) * 100,
                fixed_income: Number(allocation.fixed_income) * 100
              }))}
            />
            <CountryBreakdown
              name={`ia_regional_breakdown_${fund.id}`}
              data={mapRegionalData(
                cachedRegionalBreakdown.data[`${prefix}-${fund.id}`] || {
                  equity_regional_breakdown: [],
                  fixed_income_regional_breakdown: []
                }
              )}
            />
          </>
        );
      })}
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.checkedResearchInvestments(state),
      pastPerformance:
        selectors.investmentAnalyserSelectors.pastPerformance(state),
      cachedAssetAllocations:
        selectors.investmentAnalyserSelectors.assetAllocation(state),
      cachedCountryBreakdown:
        selectors.investmentAnalyserSelectors.countryBreakdown(state),
      cachedSectorBreakdown:
        selectors.investmentAnalyserSelectors.sectorBreakdown(state),
      cachedRegionalBreakdown:
        selectors.investmentAnalyserSelectors.regionalBreakdown(state),
      imagesLoading: selectors.reportSelectors.getImagesLoading(state),
      riskProfiler: selectors.firmSelectors.riskProfiler(state),
      riskRatings: selectors.investmentAnalyserSelectors.riskRatings(state)
    }),
    {}
  )
)(BackgroundCharts);
